import React, {FC, memo} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {InteractionParent} from '../../../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {blendColors} from '../../../../../../libs/shared/ui/src/styles/colorHelpers';
import {alphaWhite} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ThemeContextType} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';

/*
 * Props.
 */

interface CellDateProps {
  date: number;
  reminderDate?: number;
  isSelected?: boolean;
  text?: string; // Display arbitrary text instead of a date.
  shouldDisableFormatToggling?: boolean;
  // Whether a gradient overlay is rendered. Even if rendered, it is only shown if its default "display: none" styling is overridden.
  hasGradientOverlay?: boolean;
  hasP2QuickActions?: boolean;
}

/*
 * Style.
 */

interface DateStyleProps {
  $isSelected?: boolean;
  $hasReminderDate?: boolean;
  $hasP2QuickActions: boolean;
}

export const StyledCellDateDiv = styled.div<DateStyleProps>`
  grid-area: top-date;
  display: flex;
  align-items: center;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  line-height: 14px;
  color: ${(p) => getDateColor(p)};

  transform: translateY(0.5px);

  text-align: right;
  justify-self: right;
  ${(p) => addP2QuickActionsStyles(p.$hasP2QuickActions, true)}

  &:hover {
    color: ${(p) => getDateColor(p, true)};
  }
`;

interface InteractiveDateTimeFormatterContainerProps {
  $hasP2QuickActions: boolean;
}

export const StyledInteractiveDateTimeFormatterContainer = styled(
  InteractiveDateTimeFormatterContainer,
)<InteractiveDateTimeFormatterContainerProps>`
  ${(p) => addP2QuickActionsStyles(p.$hasP2QuickActions)}
`;

function addP2QuickActionsStyles(hasP2QuickActions: boolean, isCellDateDiv?: boolean) {
  if (hasP2QuickActions) {
    if (isCellDateDiv) {
      return css`
        margin-right: 4px;
      `;
    }

    return css`
      width: max-content;
    `;
  }
  return '';
}

interface StyledCellDateGradientDivProps {
  $isSelected?: boolean;
}

export const StyledCellDateGradientOverlay = styled.div<StyledCellDateGradientDivProps>`
  display: none;

  grid-row-start: top-date;
  grid-column-start: top-date;
  grid-row-end: top-right;
  grid-column-end: top-right;

  justify-self: right;
  width: 70px;
  height: 26px;
  margin-right: 10px;
  z-index: 1;

  background: ${(p) =>
    p.$isSelected
      ? `linear-gradient(270deg, ${p.theme.palette.blue.shade40} 50%, ${alphaWhite(0)} 100%)`
      : `linear-gradient(270deg, ${blendColors(
          p.theme.greys.shade20,
          p.theme.alphas.gray10,
        )} 50%, ${alphaWhite(0)} 100%)`};
`;

function getDateColor(props: DateStyleProps & ThemeContextType, isHovering?: boolean) {
  if (props.$isSelected) {
    return isHovering ? props.theme.invertedText : props.theme.invertedSubtext;
  }

  if (props.$hasReminderDate) {
    return props.theme.palette.orange.shade40;
  }

  return isHovering ? props.theme.greys.shade80 : props.theme.greys.shade70;
}

const StyledIconDiv = styled.div<Pick<DateStyleProps, '$isSelected'>>`
  margin: 2px;
  color: ${(p) => (p.$isSelected ? p.theme.alphas.white50 : p.theme.palette.orange.shade40)};
`;

/*
 * Component.
 */

export const CellDate: FC<CellDateProps> = memo((props) => {
  const interactionId = props.reminderDate ? `cell-date-reminder` : `cell-date`;

  return (
    <InteractionParent interactionId={interactionId}>
      <StyledCellDateDiv
        $isSelected={props.isSelected}
        $hasReminderDate={Boolean(props.reminderDate)}
        $hasP2QuickActions={Boolean(props.hasP2QuickActions)}
      >
        {renderDate(props)}
      </StyledCellDateDiv>
      {props.hasGradientOverlay && <StyledCellDateGradientOverlay $isSelected={props.isSelected} />}
    </InteractionParent>
  );
});

/*
 * Helpers.
 */

function renderDate(props: CellDateProps) {
  if (props.text !== undefined) {
    return <div>{props.text}</div>;
  }

  // If no reminder date was provided, render the normal date.
  if (!props.reminderDate) {
    return (
      <StyledInteractiveDateTimeFormatterContainer
        value={props.date}
        shouldDisableFormatToggling={props.shouldDisableFormatToggling}
        $hasP2QuickActions={Boolean(props.hasP2QuickActions)}
      />
    );
  }

  // Otherwise, render icon+date.
  return (
    <>
      <StyledIconDiv $isSelected={props.isSelected}>
        <Icon name="clockTiny" />
      </StyledIconDiv>
      <StyledInteractiveDateTimeFormatterContainer
        value={props.reminderDate}
        shouldDisableFormatToggling={props.shouldDisableFormatToggling}
        $hasP2QuickActions={Boolean(props.hasP2QuickActions)}
      />
    </>
  );
}
